import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Wallet, Calendar, CreditCard, FileText, AlertTriangle, Info,
  CheckCircle2, Hash, CalendarClock, Printer,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn, formatMXN, toISODate } from "@/lib/utils";
import type { ApiError } from "@/lib/api";
import { abrirReciboPago } from "@/features/config/api";
import { useAplicarPago } from "./api";
import type { FacturaResponse, MetodoPago } from "./schemas";
import { METODO_LABEL } from "./helpers";
import { useCreatePromesa } from "@/features/promesas/api";
import { useReanclarCiclo, useDescartarRevisionCiclo } from "@/features/contratos/api";
import { useAuthStore } from "@/lib/auth";
import { getTz } from "@/lib/tz";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  factura: FacturaResponse;
};

/**
 * Modal para registrar pago sobre una factura pendiente.
 *
 *   - Monto default = saldo pendiente (pago completo en 1 click).
 *   - Método default = "transferencia" (el más común en ISPs MX).
 *   - Auto-reactivación si el cliente tenía contratos suspendidos y ya
 *     no tiene deuda vencida (lo maneja cobranza_service del backend).
 *
 * Backend devuelve metadata rica: saldo_despues, estatus_despues,
 * contratos_reactivados. La usamos para el toast.
 */
type Modo = "pago" | "promesa";

function todayPlusDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toISODate(d);
}

/**
 * F100 — Calcula el "próximo día de corte" del cliente a partir de
 * la fecha de vencimiento de la factura actual. Heurística simple:
 * el ciclo es mensual, así que +30 días desde la fecha de vencimiento
 * de esta factura es ~el próximo corte.
 */
function nextCutoffISO(facturaFechaVencimiento: string): string {
  const v = new Date(facturaFechaVencimiento);
  v.setDate(v.getDate() + 30);
  return toISODate(v);
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-MX", {
    timeZone: getTz(),
    day: "2-digit", month: "short",
  });
}

export function AplicarPagoDialog({ open, onOpenChange, factura }: Props) {
  const mut = useAplicarPago();
  const promesaMut = useCreatePromesa();

  const saldo = Number(factura.saldo);
  // F95 — toggle Pago/Promesa. Mismo dialog, dos resoluciones.
  const [modo, setModo] = useState<Modo>("pago");
  const [monto, setMonto] = useState(factura.saldo);
  const [metodo, setMetodo] = useState<MetodoPago>("transferencia");
  const [referencia, setReferencia] = useState("");
  const [fecha, setFecha] = useState(toISODate(new Date()));
  const [notas, setNotas] = useState("");
  // Promesa-only
  const [fechaPrometida, setFechaPrometida] = useState(todayPlusDays(7));
  const [motivoPromesa, setMotivoPromesa] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  // rc36 — vista de éxito post-pago con botón Imprimir
  const [successPago, setSuccessPago] = useState<null | {
    pago_id: string;
    pago_folio: string;
    monto: number;
    saldo_despues: number;
    estatus_despues: string;
  }>(null);

  // rc90 — pregunta de re-anclaje de corte tras pago tardío (SOLO admins;
  // los cajeros no la ven y el corte se queda tal cual).
  const user = useAuthStore((s) => s.user);
  const esAdmin = ["administrador", "superadmin"].includes(user?.rol?.nombre ?? "");
  const reanclarMut = useReanclarCiclo();
  const descartarMut = useDescartarRevisionCiclo();
  // null = sin responder · "reanclado" | "conservado" = ya decidió
  const [corteResuelto, setCorteResuelto] = useState<null | "reanclado" | "conservado">(null);

  // Reset al abrir con nueva factura (por si el user reabre sin cerrar).
  // rc39 fix: dep era [open, factura.saldo] pero post-pago factura.saldo cambia
  // (React Query refetch) → effect disparaba → reseteaba successPago a null →
  // modal volvía al form en vez de mostrar la pantalla de éxito.
  // Ahora dep es [open, factura.id] — solo reset al abrir nuevo o cambiar factura.
  useEffect(() => {
    if (open) {
      setModo("pago");
      setMonto(factura.saldo);
      setMetodo("transferencia");
      setReferencia("");
      setFecha(toISODate(new Date()));
      setNotas("");
      setFechaPrometida(todayPlusDays(7));
      setMotivoPromesa("");
      setFormError(null);
      setSuccessPago(null);
      setCorteResuelto(null);
      mut.reset();
      promesaMut.reset();
      reanclarMut.reset();
      descartarMut.reset();
    }
  }, [open, factura.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const isPending = mut.isPending || promesaMut.isPending;

  function close() {
    if (isPending) return;
    onOpenChange(false);
  }

  async function handleSubmit() {
    setFormError(null);
    const montoNum = Number(monto);
    if (!Number.isFinite(montoNum) || montoNum <= 0) {
      setFormError("El monto debe ser mayor a 0.");
      return;
    }
    if (montoNum > saldo + 0.001) {  // margen float
      setFormError(
        `El monto excede el saldo pendiente (${formatMXN(saldo)}).`,
      );
      return;
    }

    // F95 — rama promesa
    if (modo === "promesa") {
      if (!fechaPrometida) {
        setFormError("La fecha prometida es obligatoria.");
        return;
      }
      try {
        await promesaMut.mutateAsync({
          cliente_id: factura.cliente_id,
          factura_id: factura.id,
          fecha_prometida: fechaPrometida,
          monto_prometido: montoNum,
          motivo: motivoPromesa.trim() || null,
        });
        toast.success("Promesa de pago registrada", {
          description: `Factura ${factura.folio} · prometido ${formatMXN(montoNum)} para ${fechaPrometida}`,
        });
        onOpenChange(false);
      } catch (err) {
        setFormError(mapBackendError(err as ApiError));
      }
      return;
    }

    if (!fecha) {
      setFormError("La fecha es obligatoria.");
      return;
    }
    try {
      const res = await mut.mutateAsync({
        cliente_id: factura.cliente_id,
        factura_id: factura.id,
        monto: String(montoNum),
        metodo,
        referencia: referencia.trim() || null,
        fecha,
        notas: notas.trim() || null,
      });
      // Toast rico: monto + saldo después + estatus
      const saldoDespues = Number(res.factura_saldo_despues ?? 0);
      const estatusDespues = res.factura_estatus_despues ?? "pendiente";
      const estatusText =
        estatusDespues === "pagada"
          ? "factura pagada ✓"
          : estatusDespues === "parcial"
            ? `saldo restante ${formatMXN(saldoDespues)}`
            : `saldo: ${formatMXN(saldoDespues)}`;
      // rc37 — en lugar de cerrar y mostrar toast, mostrar pantalla de éxito
      // dentro del modal con botón explícito "Imprimir recibo"
      setSuccessPago({
        pago_id: res.pago.id,
        pago_folio: res.pago.folio,
        monto: montoNum,
        saldo_despues: saldoDespues,
        estatus_despues: estatusDespues,
      });
      // Toast solo informativo del estado de la factura (sin acción)
      toast.success(`Pago de ${formatMXN(montoNum)} aplicado`, {
        description: `Folio ${res.pago.folio} · ${estatusText}`,
      });
      // Toast secundario si hubo auto-reactivación (celebrar al operador)
      if (res.contratos_reactivados_count > 0) {
        toast.info(
          `${res.contratos_reactivados_count} contrato${res.contratos_reactivados_count === 1 ? "" : "s"} reactivado${res.contratos_reactivados_count === 1 ? "" : "s"} automáticamente`,
          {
            description: res.contratos_reactivados_codigos.join(", "),
          },
        );
      }
      // NO cerrar el modal — el cajero ve la pantalla de éxito y decide imprimir
    } catch (err) {
      const apiErr = err as ApiError;
      setFormError(mapBackendError(apiErr));
    }
  }

  const montoNum = Number(monto);
  const esPagoCompleto = Number.isFinite(montoNum) && Math.abs(montoNum - saldo) < 0.01;
  const esPagoParcial = Number.isFinite(montoNum) && montoNum > 0 && montoNum < saldo;

  // rc37 — vista de éxito post-pago con botón Imprimir
  if (successPago) {
    // rc90 — ¿el pago fue tardío? (factura mensualidad ya vencida al momento
    // del pago). Si además el usuario es admin, se pregunta si actualizar el
    // corte al día del pago (29-31 → cap 28). Responder (Sí o No) limpia la
    // revisión pendiente en backend — no vuelve a aparecer en Cobranza.
    const diaPago = Number(fecha.slice(8, 10)) || new Date().getDate();
    const diaSugerido = Math.min(diaPago, 28);
    // Solo preguntar cuando la factura quedó PAGADA (un pago parcial deja la
    // vencida viva y al cliente sin reactivar — re-anclar ahí sería prematuro).
    const pagoTardio =
      esAdmin &&
      successPago.estatus_despues === "pagada" &&
      factura.contrato_id != null &&
      (factura.tipo ?? "mensualidad") === "mensualidad" &&
      factura.fecha_vencimiento < fecha;
    const corteMutPending = reanclarMut.isPending || descartarMut.isPending;

    async function resolverCorte(actualizar: boolean) {
      if (!factura.contrato_id) return;
      try {
        if (actualizar) {
          await reanclarMut.mutateAsync({
            contratoId: factura.contrato_id,
            dia: diaSugerido,
            fechaPago: fecha,
            motivo: `Pago tardío de ${factura.folio} aplicado el ${fecha}`,
          });
          setCorteResuelto("reanclado");
          toast.success(`Fecha de corte actualizada al día ${diaSugerido}`, {
            description: "La próxima mensualidad se emitirá para el mes siguiente.",
          });
        } else {
          await descartarMut.mutateAsync(factura.contrato_id);
          setCorteResuelto("conservado");
        }
      } catch (err) {
        toast.error("No se pudo actualizar el corte", {
          description: (err as ApiError).message,
        });
      }
    }

    return (
      <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}
              size="md" closeOnBackdrop={true}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 size={18} className="text-emerald-600 dark:text-emerald-500" />
                Pago registrado
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col items-center gap-3 py-6">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/40">
              <CheckCircle2 size={36} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-400 font-mono">
                {formatMXN(successPago.monto)}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Folio <span className="font-mono font-medium">{successPago.pago_folio}</span> · Factura {factura.folio}
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                {successPago.estatus_despues === "pagada"
                  ? "Factura pagada ✓"
                  : `Saldo restante: ${formatMXN(successPago.saldo_despues)}`}
              </div>
            </div>
          </div>

          {/* rc90 — pregunta de re-anclaje de corte (solo admins, pago tardío) */}
          {pagoTardio && corteResuelto === null && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-900/40 dark:bg-amber-950/30">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="flex-1 text-[11px] text-amber-900 dark:text-amber-300">
                  <div className="font-semibold text-xs mb-0.5">
                    Pago tardío — la factura venció el {factura.fecha_vencimiento}
                  </div>
                  ¿Actualizar la fecha de corte del contrato al día {diaSugerido}?
                  Su próxima mensualidad se emitiría para el {diaSugerido} del mes
                  siguiente (el pago le cubre un mes desde hoy). Si la conservas,
                  el ciclo sigue con el corte actual.
                </div>
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <Button
                  size="sm" variant="outline"
                  disabled={corteMutPending}
                  onClick={() => resolverCorte(false)}
                >
                  No, conservar corte
                </Button>
                <Button
                  size="sm"
                  disabled={corteMutPending}
                  onClick={() => resolverCorte(true)}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <CalendarClock size={12} />
                  {corteMutPending ? "Aplicando…" : `Sí, corte al día ${diaSugerido}`}
                </Button>
              </div>
            </div>
          )}
          {pagoTardio && corteResuelto === "reanclado" && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-2 text-[11px] text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300">
              ✓ Fecha de corte actualizada al día {diaSugerido}. La próxima
              mensualidad se emitirá para el mes siguiente.
            </div>
          )}
          {pagoTardio && corteResuelto === "conservado" && (
            <div className="rounded-md border bg-muted/20 p-2 text-[11px] text-muted-foreground">
              Se conservó el día de corte actual del contrato.
            </div>
          )}

          <DialogFooter>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cerrar
            </Button>
            <Button
              size="sm"
              onClick={() => {
                abrirReciboPago(successPago.pago_id).catch((err) =>
                  toast.error("Error al abrir recibo", { description: err.message })
                );
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Printer size={14} /> Imprimir recibo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}
            size="md" closeOnBackdrop={!isPending}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2">
              {modo === "pago" ? (
                <><Wallet size={16} className="text-emerald-600 dark:text-emerald-500" /> Resolver factura</>
              ) : (
                <><CalendarClock size={16} className="text-amber-600 dark:text-amber-500" /> Resolver factura</>
              )}
            </span>
          </DialogTitle>
          <DialogDescription>
            Factura <span className="font-mono font-medium">{factura.folio}</span> ·
            {" "}Saldo pendiente: <span className="font-semibold text-destructive">
              {formatMXN(saldo)}
            </span>
          </DialogDescription>
        </DialogHeader>

        {/* F95 — Toggle Pago/Promesa */}
        <div
          className="grid grid-cols-2 gap-1 rounded-md bg-muted/40 p-1"
          role="tablist"
        >
          <button
            type="button"
            role="tab"
            aria-selected={modo === "pago"}
            onClick={() => setModo("pago")}
            disabled={isPending}
            className={cn(
              "rounded-[6px] px-3 py-2 text-sm font-medium transition-colors",
              modo === "pago"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Wallet size={12} className="mr-1.5 inline" />
            Aplicar pago
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={modo === "promesa"}
            onClick={() => setModo("promesa")}
            disabled={isPending}
            className={cn(
              "rounded-[6px] px-3 py-2 text-sm font-medium transition-colors",
              modo === "promesa"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <CalendarClock size={12} className="mr-1.5 inline" />
            Otorgar promesa
          </button>
        </div>

        <div className="flex flex-col gap-3 py-2">
          {/* Monto */}
          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
              <Wallet size={11} /> Monto a aplicar
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="number" step="0.01" min={0.01}
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
                disabled={isPending}
                className="text-base font-mono"
              />
              <Button
                size="sm" variant="outline" type="button"
                onClick={() => setMonto(factura.saldo)}
                disabled={isPending || esPagoCompleto}
              >
                Pagar total
              </Button>
            </div>
            <span className="text-[10px] text-muted-foreground">
              {esPagoCompleto && (
                <span className="text-emerald-700 dark:text-emerald-400 font-medium">
                  ✓ Pago completo — la factura quedará pagada.
                </span>
              )}
              {esPagoParcial && (
                <span className="text-amber-700 dark:text-amber-400">
                  ⚠️ Pago parcial — saldo restante: {formatMXN(saldo - montoNum)}
                </span>
              )}
            </span>
          </div>

          {modo === "pago" && (
            <>
              {/* Método + Fecha */}
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <CreditCard size={11} /> Método de pago
                  </label>
                  <Select
                    value={metodo}
                    onChange={(e) => setMetodo(e.target.value as MetodoPago)}
                    disabled={isPending}
                  >
                    <option value="transferencia">{METODO_LABEL.transferencia}</option>
                    <option value="efectivo">{METODO_LABEL.efectivo}</option>
                    <option value="tarjeta">{METODO_LABEL.tarjeta}</option>
                    <option value="cheque">{METODO_LABEL.cheque}</option>
                    <option value="otro">{METODO_LABEL.otro}</option>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <Calendar size={11} /> Fecha del pago
                  </label>
                  <Input
                    type="date"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                    disabled={isPending}
                    max={toISODate(new Date())}
                  />
                </div>
              </div>

              {/* Referencia */}
              <div className="flex flex-col gap-1">
                <label className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <Hash size={11} /> Referencia / comprobante (opcional)
                </label>
                <Input
                  type="text" maxLength={100}
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                  placeholder={
                    metodo === "transferencia"
                      ? "Ej. SPEI BBVA 0012345"
                      : metodo === "tarjeta"
                        ? "Últimos 4 dígitos o autorización"
                        : metodo === "cheque"
                          ? "Número de cheque"
                          : "Opcional"
                  }
                  disabled={isPending}
                />
              </div>

              {/* Notas */}
              <div className="flex flex-col gap-1">
                <label className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <FileText size={11} /> Notas (opcional)
                </label>
                <Textarea
                  rows={2} maxLength={500}
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  placeholder="Contexto adicional del pago"
                  disabled={isPending}
                />
              </div>
            </>
          )}

          {modo === "promesa" && (
            <>
              <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
                La promesa pausa la suspensión hasta la fecha. Si no paga, el
                cliente vuelve a entrar al ciclo de cobranza al día siguiente.
                Si está suspendido, se reactiva automáticamente.
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <CalendarClock size={11} /> Fecha prometida
                  </label>
                  <Input
                    type="date"
                    value={fechaPrometida}
                    onChange={(e) => setFechaPrometida(e.target.value)}
                    min={toISODate(new Date())}
                    max={todayPlusDays(15)}
                    disabled={isPending}
                  />
                  <span className="text-[10px] text-muted-foreground">
                    Máximo 15 días desde hoy.
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <FileText size={11} /> Motivo (opcional)
                  </label>
                  <Input
                    type="text" maxLength={500}
                    value={motivoPromesa}
                    onChange={(e) => setMotivoPromesa(e.target.value)}
                    placeholder='Ej. "Cae nómina el 15"'
                    disabled={isPending}
                  />
                </div>
              </div>

              {/* F100 — Warning si la promesa cae después del próximo corte */}
              {(() => {
                const proximoCorte = nextCutoffISO(factura.fecha_vencimiento);
                if (fechaPrometida && fechaPrometida >= proximoCorte) {
                  return (
                    <div className="flex items-start gap-2 rounded-md border border-orange-400 bg-orange-50 p-2 text-[11px] text-orange-900 dark:border-orange-900/40 dark:bg-orange-950/30 dark:text-orange-300">
                      <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                      <span>
                        <strong>Cuidado:</strong> esta promesa vence después del
                        próximo corte (~{formatShortDate(proximoCorte)}). Mientras
                        tanto se generará una nueva factura. El cliente podría
                        terminar con 2 facturas pendientes — avísale.
                      </span>
                    </div>
                  );
                }
                return null;
              })()}
            </>
          )}

          {formError && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          {modo === "pago" && (
            <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-2 text-[11px] text-muted-foreground">
              <Info size={12} className="mt-0.5 shrink-0 text-primary" />
              <span>
                Si el cliente tenía contratos suspendidos por falta de pago y ya no
                tiene deuda vencida, se reactivarán automáticamente.
                El pago queda en auditoría con tu usuario.
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button size="sm" variant="ghost" onClick={close} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={isPending}
            className={cn(
              "text-white",
              modo === "pago"
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-amber-600 hover:bg-amber-700",
            )}
          >
            {modo === "pago" ? <CheckCircle2 size={12} /> : <CalendarClock size={12} />}
            {isPending
              ? modo === "pago" ? "Aplicando…" : "Registrando…"
              : modo === "pago" ? "Aplicar pago" : "Otorgar promesa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════════
function mapBackendError(err: ApiError): string {
  switch (err.code) {
    case "factura_anulada":
      return "La factura está anulada — no se puede pagar.";
    case "factura_cliente_mismatch":
      return "La factura no pertenece al cliente.";
    case "monto_excede_saldo":
      return err.message ?? "El monto excede el saldo pendiente.";
    case "cliente_not_found":
      return "Cliente no encontrado.";
    case "factura_not_found":
      return "Factura no encontrada.";
    // F95 — errores de promesa
    case "promesa_factura_duplicada":
      return "Esta factura ya tiene una promesa activa. Cancélala desde /promesas o espera a que se cumpla.";
    case "promesa_global_duplicada":
      return "El cliente ya tiene una promesa global activa.";
    case "promesa_activa_existente":
      return "El cliente ya tiene una promesa activa.";
    case "fecha_invalida":
      return "La fecha prometida no puede ser pasada.";
    case "fecha_excede_maximo":
      return err.message ?? "La fecha excede el máximo permitido.";
    case "monto_excede_deuda":
      return err.message ?? "El monto promised excede la deuda actual.";
    case "max_promesas_anio":
      return err.message ?? "El cliente ya alcanzó el máximo de promesas anuales.";
    default:
      return err.message ?? "Error desconocido del servidor.";
  }
}

